import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItemButton,
} from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MuiBox, MuiTypography } from "..";
import { usePermissions } from "../../hooks/usePermissions";
import { isDataModifiedCurrentPageSelector } from "../../redux/slices/appState/appStateSelector";
import {
  setNavigationDataClicked,
  toggleSideBar,
} from "../../redux/slices/appState/appStateSlice";
import {
  clearProjectDetails,
  clearStartChildProgramNavigation,
} from "../../redux/slices/project/projectSlice";
import { SidebarAccordionWrapper } from "./SidebarAccordion.style";
import { TIMEOUT_100 } from "../../helpers/constants";

export interface SidebarAccordionItem {
  itemName: string;
  destinationPath: string;
  children?: SidebarAccordionItem[];
}

export interface SidebarAccordionProps {
  accordionTitle: string;
  accordionItemArray: SidebarAccordionItem[];
  expanded?: boolean;
  expandIcon?: React.ReactNode;
  showAccordion?: boolean;
  selectedDestination: string;
  imgSrc?: string;
  id: string;
  imgAltText?: string;
  onAccordionToggle: (id: string) => void;
}

export const SidebarAccordion = ({
  id,
  onAccordionToggle,
  accordionTitle,
  accordionItemArray,
  expanded = false,
  expandIcon = <ExpandMoreIcon color={"primary"} />,
  showAccordion = true,
  selectedDestination,
  imgSrc,
  imgAltText,
}: SidebarAccordionProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPagePermission } = usePermissions();

  const isDataModifiedCurrentPage = useSelector(
    isDataModifiedCurrentPageSelector,
  );

  const [expandedNestedIndex, setExpandedNestedIndex] = useState<number | null>(
    null,
  );

  const handleMenuItemClick = (destinationPath: string) => {
    if (isDataModifiedCurrentPage) {
      dispatch(
        setNavigationDataClicked({
          onNavigationClicked: true,
          navigationPath: destinationPath,
        }),
      );
      return;
    } else {
      dispatch(toggleSideBar({ isOpen: false, destinationPath }));
      dispatch(clearStartChildProgramNavigation()); // Clear Child Program Navigation state on sidebar navigation
      dispatch(clearProjectDetails()); // Clear Project Details on sidebar navigation
      setTimeout(() => {
        navigate(destinationPath);
      }, TIMEOUT_100);
    }
  };

  const isSelected = (destinationPath: string) => {
    return destinationPath === selectedDestination ? "selected" : "";
  };

  const hasSelectedPath = (items: SidebarAccordionItem[] = []): boolean => {
    return items.some((item) => {
      if (item.destinationPath === selectedDestination) {
        return true;
      }

      if (item.children && item.children.length > 0) {
        return hasSelectedPath(item.children); // recursion
      }

      return false;
    });
  };

  return (
    <SidebarAccordionWrapper>
      {showAccordion ? (
        <Accordion
          elevation={0}
          disableGutters
          id="accordion-root"
          expanded={expanded}
          onChange={() => onAccordionToggle(id)}
        >
          <AccordionSummary
            expandIcon={expandIcon}
            id="panel1"
            className={`side-bar__accordion root-accordion ${
              hasSelectedPath(accordionItemArray) ||
              expandedNestedIndex !== null
                ? "selected"
                : ""
            }`}
          >
            <MuiBox className="icon-wrapper">
              <img src={imgSrc} className="icon" alt={imgAltText} />
            </MuiBox>
            <MuiTypography variant={"body1Medium"}>
              {accordionTitle}
            </MuiTypography>
          </AccordionSummary>
          <MuiBox className="item-wrapper">
            {accordionItemArray.map((item, index) => {
              const hasChildren = !!item.children?.length;

              return hasChildren ? (
                <Accordion
                  key={`${item.itemName}-${index}`}
                  elevation={0}
                  disableGutters
                  className="nested-accordion"
                  expanded={expandedNestedIndex === index}
                  onChange={(_, isExpanded) =>
                    setExpandedNestedIndex(isExpanded ? index : null)
                  }
                >
                  <AccordionSummary
                    expandIcon={expandIcon}
                    className={`side-bar__accordion nested-accordion ${
                      hasSelectedPath([item]) ? "selected" : ""
                    }`}
                  >
                    <MuiBox className={`item-indicator`} />
                    <MuiTypography
                      className={`item-text`}
                      variant="body1Medium"
                    >
                      {item.itemName}
                    </MuiTypography>
                  </AccordionSummary>

                  <AccordionDetails className="nested-items">
                    {item.children?.map(
                      (child, childIndex) =>
                        hasPagePermission(child.destinationPath) && (
                          <ListItemButton
                            key={`${child.itemName}-${childIndex}`}
                            className={`item nested ${isSelected(child.destinationPath)}`}
                            disableGutters
                            onClick={() =>
                              handleMenuItemClick(child.destinationPath)
                            }
                          >
                            <MuiBox
                              className={`item-indicator ${isSelected(child.destinationPath)}`}
                            />
                            <MuiTypography
                              className={`item-text ${isSelected(child.destinationPath)}`}
                              variant="body1Medium"
                              component={"p"}
                            >
                              {child.itemName}
                            </MuiTypography>
                          </ListItemButton>
                        ),
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : (
                hasPagePermission(item.destinationPath) && (
                  <ListItemButton
                    className={`item ${isSelected(item.destinationPath)}`}
                    key={`${item.itemName}-${index}`}
                    disableGutters
                    id={`${item.itemName}-${index}`}
                    onClick={() => handleMenuItemClick(item.destinationPath)}
                  >
                    <MuiBox
                      className={`item-indicator ${isSelected(item.destinationPath)}`}
                    />
                    <MuiTypography
                      className={`item-text ${isSelected(item.destinationPath)}`}
                      variant="body1Medium"
                      component={"p"}
                    >
                      {item.itemName}
                    </MuiTypography>
                  </ListItemButton>
                )
              );
            })}
          </MuiBox>
        </Accordion>
      ) : (
        <ListItemButton
          id="Single-item-name"
          disableGutters
          className="single-item"
          onClick={() =>
            handleMenuItemClick(accordionItemArray[0].destinationPath)
          }
        >
          <MuiBox className="icon-wrapper">
            <img src={imgSrc} className="icon" alt={imgAltText} />
          </MuiBox>
          <MuiTypography variant="body1Medium" className="item-text">
            {accordionItemArray[0].itemName}
          </MuiTypography>
        </ListItemButton>
      )}
    </SidebarAccordionWrapper>
  );
};
